import { defineStore } from "pinia";
import { api } from "../lib/api";
import { setAccessToken, setRefreshToken, getRefreshToken } from "../lib/session-token";
import type { SessionUser } from "../lib/types";

// Respuesta de sesión de la API v3 (plana, no envuelta).
interface V3Session {
  access_token: string;
  refresh_token?: string;
  user: SessionUser;
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null as SessionUser | null,
    initialized: false,
  }),
  getters: {
    isAuthenticated: (state) => Boolean(state.user),
  },
  actions: {
    applySession(session: V3Session) {
      setAccessToken(session.access_token);
      if (session.refresh_token) setRefreshToken(session.refresh_token);
      this.user = session.user;
    },
    async initialize() {
      if (this.initialized) return;
      await this.refresh();
      this.initialized = true;
    },
    async login(email: string, password: string) {
      const response = await api.post<V3Session>("/api/v3/auth/login", { email, password });
      this.applySession(response.data);
    },
    async requestEmailCode(email: string) {
      await api.post("/api/v3/auth/web/otp/request", { email });
    },
    async loginWithEmailCode(email: string, code: string) {
      // v3 espera el campo "otp"
      const response = await api.post<V3Session>("/api/v3/auth/web/otp/verify", {
        email,
        otp: code,
      });
      this.applySession(response.data);
    },
    async setPassword(password: string) {
      await api.post("/api/v3/auth/set-password", { password });
    },
    async refresh() {
      const rt = getRefreshToken();
      if (!rt) {
        setAccessToken(null);
        this.user = null;
        return false;
      }
      try {
        const response = await api.post<V3Session>("/api/v3/auth/refresh", {
          refresh_token: rt,
        });
        this.applySession(response.data);
        return true;
      } catch {
        setAccessToken(null);
        setRefreshToken(null);
        this.user = null;
        return false;
      }
    },
    async logout() {
      // v3 no tiene endpoint de logout: se limpia el estado local.
      setAccessToken(null);
      setRefreshToken(null);
      this.user = null;
    },
  },
});
